import { useState } from "react";
import { dirOf } from "../utils/textDir";
import { T, UI } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { useCompanyProfileData, groupSegments, segmentSource, describeCode, codeNote, displayName, titleHe, recentByKind, recentIsPartial, TRANSACTIONS_LIMIT } from "../utils/companyProfile";
import { dateNumeric } from "../utils/formatDate";
import EmptyState from "./EmptyState";
import SecEmpty from "./SecEmpty";
import { useSecStatus } from "../utils/secStatus";
import Icon from "./Icon";
import LTR from "./LTR";
import { Amount, CompactMoney } from "./Money";
import { fmtPct } from "../utils/formatters";
import { regionLabelHe } from "../constants/regions";
import { paragraphsOf, openingOf, isSubheading } from "../utils/filingText";

// ─── Company profile ─────────────────────────────────────────────────────────
// Four questions about a company the user actually holds, each answered from
// one SEC filing and each carrying the filing it came from.
//
//   מה החברה עושה          Item 1 of the annual report, in the company's words
//   מאיפה הכסף             the revenue breakdown the company itself reported
//   מי מנהל                who its ownership filings name as officers
//   מה הם עושים עם המניות   what those people did with their own shares
//
// Every tab has exactly two states: a fact with a source, or a stated empty
// state. There is no third state where a number appears without a filing
// behind it, and there is no static fallback table anywhere on this path.

// ── The source line every tab ends with ──────────────────────────────────────
// The app never navigates the user out — that rule holds across the whole
// product and this screen does not get an exception. So the original document
// is offered as something the user can take with them rather than as a link
// that leaves: the filing is named precisely, and its address can be copied in
// one press.
function SourceLine({ form, filed, url, label = "מקור" }) {
  const [copied, setCopied] = useState(false);
  if (!url && !form) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the address stays on screen either
      // way, so there is nothing to recover from and nothing to announce.
    }
  };

  return (
    <div style={{
      marginTop: T.space.md, paddingTop: T.space.sm,
      borderTop: `1px solid ${UI.line.hairline}`,
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: T.scale[1],
      fontSize: 12, color: UI.text.muted,
    }}>
      <Icon name="clipboard" size={13} color={UI.text.muted}/>
      <span>{label}:</span>
      <LTR style={{ fontWeight: 700, color: UI.text.secondary }}>SEC EDGAR</LTR>
      {form && <><span>·</span><LTR style={{ color: UI.text.secondary }}>{form}</LTR></>}
      {filed && <><span>·</span><span>הוגש {dateNumeric(filed)}</span></>}
      {url && (
        <button onClick={copy} style={{
          background: copied ? UI.state.surface : "transparent",
          color: copied ? UI.state.fg : UI.text.secondary,
          border: `1px solid ${copied ? UI.state.line : UI.line.hairline}`,
          borderRadius: T.radius.pill, padding: "2px 10px",
          fontSize: 11.5, fontWeight: 700, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <Icon name={copied ? "check" : "link"} size={12} color={copied ? UI.state.fg : UI.text.secondary}/>
          {copied ? "הועתק" : "העתק קישור לדוח"}
        </button>
      )}
    </div>
  );
}

// ── Money, at the scale a segment table needs ────────────────────────────────
// Through the shared CompactMoney (sprint 17): the minus is "−", the "$"
// follows the one currency rule.
const money = (n, size = 16) => <CompactMoney value={n} size={size}/>;

const shares = (n) =>
  n == null || !Number.isFinite(n) ? "—" : Math.round(n).toLocaleString("en-US");

// ═════════════════════════════════════════════════════════════════════════════
// Tab 1 — what the company does
// ═════════════════════════════════════════════════════════════════════════════
function WhatItDoes({ business }) {
  const [open, setOpen] = useState(false);
  if (!business || (!business.description_he && !business.description_en)) {
    return (
      <EmptyState
        icon="building"
        line="אין תיאור עסקי זמין לחברה זו"
        sub="התיאור נלקח מסעיף העסקים בדוח השנתי. חלק מהחברות בנו את הדוח שלהן במבנה שאינו כולל סעיף כזה, ואין ממה לצטט."
      />
    );
  }

  // The Hebrew is a translation of the English, produced by a model. When it
  // is missing the English stands on its own — the filing's actual words are
  // always preferable to nothing, and the label says which one is on screen.
  const he = business.description_he;
  // Page footers, the section heading and ® / ™ out (utils/filingText.js).
  // Two paragraphs first, the rest on "קרא עוד" (2026-09-26).
  const paras = paragraphsOf(he || business.description_en);
  const opening = openingOf(paras);
  const shown = open ? paras : opening;

  return (
    <div>
      <div style={{
        background: UI.surface.sunken, borderRadius: T.radius.lg,
        padding: `${T.space.md}px`, border: `1px solid ${UI.line.hairline}`,
      }}>
        <div dir={he ? "rtl" : "ltr"} style={{
          ...TYPE.body, fontSize: 15, lineHeight: 1.85,
          display: "flex", flexDirection: "column", gap: 10,
          ...(he ? null : { textAlign: "left", unicodeBidi: "isolate" }),
        }}>
          {shown.map((p, i) => isSubheading(p)
            ? <div key={i} dir={he ? "rtl" : "ltr"} style={{ fontWeight: 800, color: UI.text.primary, marginTop: i ? 6 : 0 }}>{p}</div>
            : <p key={i} dir={dirOf(p)} style={{ margin: 0 }}>{p}</p>)}
        </div>
        {paras.length > opening.length && (
          <button onClick={() => setOpen(o => !o)} aria-expanded={open}
            style={{ marginTop: 10, minHeight: 44, padding: "0 4px", background: "none", border: "none", color: UI.interactive, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {open ? "הצג פחות" : "קרא עוד"}
          </button>
        )}
      </div>

      <div style={{ marginTop: T.space.sm, fontSize: 12, color: UI.text.muted, lineHeight: 1.6 }}>
        {he
          ? "תרגום לעברית של סעיף העסקים בדוח השנתי · קטע מהדוח, לא סיכום"
          : "סעיף העסקים בדוח השנתי, בשפת המקור · התרגום לעברית אינו זמין כרגע"}
        {business.fiscal_year ? ` · שנת דיווח ${business.fiscal_year}` : ""}
      </div>

      <SourceLine form={business.source_form} filed={business.filed_date} url={business.source_url}/>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Tab 2 — where the money comes from
// ═════════════════════════════════════════════════════════════════════════════
function SegmentGroup({ title, hint, group, geographic = false }) {
  if (!group.items.length) return null;
  const [latest, prior] = group.years;

  return (
    <div style={{ marginBottom: T.space.lg }}>
      <div style={{ ...TYPE.headingSub, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: UI.text.muted, marginBottom: T.space.sm, lineHeight: 1.6 }}>
        {hint} · שנת כספים <LTR>{latest}</LTR>
        {prior != null && <> מול <LTR>{prior}</LTR></>}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: T.scale[1] }}>
        {group.items.map(item => {
          // Bars are scaled against the LARGEST ROW, never against a computed
          // total. Several filers report a sub-total alongside its own
          // components — JPMorgan files "Total International" beside EMEA,
          // Asia-Pacific and Latin America — so summing the rows double-counts
          // and any share-of-revenue figure drawn from that sum would be a
          // number the company never reported.
          const pct = group.max > 0 ? Math.max(2, (item.revenue / group.max) * 100) : 0;
          const up = item.growth != null && item.growth >= 0;
          return (
            <div key={item.name} style={{
              background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`,
              borderRadius: T.radius.lg, padding: `${T.scale[2]}px ${T.scale[4]}px`,
            }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: T.space.sm }}>
                <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: UI.text.primary, minWidth: 0 }}>
                  {/* A region the company named is shown in Hebrew (sprint 13, 1.7) */}
                  {regionLabelHe(item.name, { geographic })
                    ? <span title={item.name}>{regionLabelHe(item.name, { geographic })}</span>
                    : <LTR>{item.name}</LTR>}
                </span>
                <span style={{ flexShrink: 0 }}>{money(item.revenue)}</span>
              </div>

              <div style={{
                height: 6, borderRadius: T.radius.pill, background: UI.surface.sunken,
                margin: `${T.scale[1]}px 0 ${T.scale[0]}px`, overflow: "hidden",
              }}>
                <div style={{ width: `${pct}%`, height: "100%", background: UI.series[1], borderRadius: T.radius.pill }}/>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: T.scale[2], flexWrap: "wrap", fontSize: 12 }}>
                {item.growth != null ? (
                  // A year-over-year revenue figure is a number that rose or
                  // fell, which is the one meaning green and red carry here.
                  <LTR className="si-num" style={{ fontWeight: 700, color: up ? UI.gain : UI.loss }}>
                    {fmtPct(item.growth, { decimals: 1 })}
                  </LTR>
                ) : (
                  <span style={{ color: UI.text.muted }}>אין שנת השוואה בדוח</span>
                )}
                {item.operating_income != null && (
                  <span style={{ color: UI.text.muted }}>
                    רווח תפעולי {money(item.operating_income, 12)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WhereTheMoneyComesFrom({ segments }) {
  const byBusiness = groupSegments(segments, "business");
  const byGeo      = groupSegments(segments, "geographic");
  const src = segmentSource(segments);

  if (!byBusiness.items.length && !byGeo.items.length) {
    return (
      <EmptyState
        icon="coins"
        line="אין פילוח הכנסות זמין לחברה זו"
        sub="הפילוח נלקח מהדוח השנתי שהחברה הגישה ל-SEC. חברה שאינה מתייגת את הכנסותיה לפי מגזר או אזור אינה מופיעה כאן."
      />
    );
  }

  return (
    <div>
      <SegmentGroup
        title="לפי מגזר עסקי"
        hint="כפי שהחברה עצמה חילקה את הכנסותיה בדוח"
        group={byBusiness}
      />
      <SegmentGroup
        title="לפי אזור גיאוגרפי"
        geographic
        hint="כפי שהחברה עצמה חילקה את הכנסותיה בדוח"
        group={byGeo}
      />
      <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6 }}>
        אורך העמודה מציג יחס בין השורות בלבד. חלק מהחברות מדווחות גם סכום־ביניים
        וגם את מרכיביו, ולכן סכימה של השורות אינה בהכרח סך ההכנסות ואין כאן אחוז מסך.
      </div>
      <SourceLine form="דוח שנתי" filed={src?.filed} url={src?.url}/>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Tab 3 — who runs it
// ═════════════════════════════════════════════════════════════════════════════
// A filed title in Hebrew when it is a common one, with the filer's words in
// the tooltip; otherwise as filed (sprint 14, 4.3).
function Title({ raw, style }) {
  const he = titleHe(raw);
  return he
    ? <span title={raw} style={style}>{he}</span>
    : <LTR style={style}>{raw}</LTR>;
}

function PersonRow({ p }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      gap: T.space.sm, padding: `${T.scale[2]}px 0`,
      borderBottom: `1px solid ${UI.line.hairline}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...TYPE.bodySmall, fontWeight: 700, color: UI.text.primary }}>
          <LTR title={p.name}>{displayName(p.name)}</LTR>
        </div>
        {p.title && (
          <div style={{ fontSize: 12.5, color: UI.text.secondary, marginTop: 1 }}>
            <Title raw={p.title}/>
          </div>
        )}
      </div>
      {(p.first_seen_date || p.last_seen_date) && (
        <div style={{ fontSize: 11.5, color: UI.text.muted, textAlign: "left", flexShrink: 0, lineHeight: 1.5 }}>
          {p.first_seen_date && <div>מ־{dateNumeric(p.first_seen_date)}</div>}
          {p.last_seen_date && <div>עד {dateNumeric(p.last_seen_date)}</div>}
        </div>
      )}
    </div>
  );
}

function WhoRunsIt({ officers }) {
  const people    = officers.filter(o => o.is_officer);
  const directors = officers.filter(o => o.is_director && !o.is_officer);
  const holders   = officers.filter(o => o.is_ten_percent_owner && !o.is_officer && !o.is_director);

  if (!officers.length) {
    return (
      <EmptyState
        icon="person"
        line="אין נושאי משרה רשומים לחברה זו"
        sub="השמות נלקחים מטפסי הבעלות שהחברה מגישה ל-SEC. עד שיוגש טופס כזה אין לנו שם לרשום."
      />
    );
  }

  const src = officers.find(o => o.source_url);

  return (
    <div>
      {/* The single most important sentence on this tab. The dates are not
          employment dates and there is no way to derive one from EDGAR, so
          the interface says what they actually are before showing them. */}
      <div style={{
        background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`,
        borderRadius: T.radius.lg, padding: `${T.scale[2]}px ${T.scale[4]}px`,
        fontSize: 12.5, color: UI.text.secondary, lineHeight: 1.65, marginBottom: T.space.md,
      }}>
        התאריכים הם ההופעה הראשונה והאחרונה של אותו אדם בטפסי הבעלות שקראנו,
        ולא תאריכי תחילת תפקיד או סיומו. ה-SEC אינה מפרסמת תאריך מינוי.
      </div>

      {people.length > 0 && (
        <div style={{ marginBottom: T.space.md }}>
          <div style={{ ...TYPE.headingSub, marginBottom: T.scale[1] }}>נושאי משרה</div>
          {people.map(p => <PersonRow key={p.owner_cik} p={p}/>)}
        </div>
      )}

      {/* Directors are kept apart from officers rather than merged into one
          list: they are a different relationship to the company, and the
          filing states which is which. */}
      {directors.length > 0 && (
        <div style={{ marginBottom: T.space.md }}>
          <div style={{ ...TYPE.headingSub, marginBottom: T.scale[1] }}>דירקטורים שאינם נושאי משרה</div>
          {directors.map(p => <PersonRow key={p.owner_cik} p={p}/>)}
        </div>
      )}

      {holders.length > 0 && (
        <div style={{ marginBottom: T.space.md }}>
          <div style={{ ...TYPE.headingSub, marginBottom: T.scale[1] }}>בעלי מניות מעל 10%</div>
          {holders.map(p => <PersonRow key={p.owner_cik} p={p}/>)}
        </div>
      )}

      <SourceLine form="טפסי בעלות 3 · 4 · 5" filed={src?.last_seen_date} url={src?.source_url}/>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Tab 4 — what they do with their shares
// ═════════════════════════════════════════════════════════════════════════════
function WhatTheyDo({ transactions }) {
  const [showAll, setShowAll] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  if (!transactions.length) {
    return (
      <EmptyState
        icon="list"
        line="לא נרשמו עסקאות בעלי עניין בשנתיים האחרונות"
        sub="נספרות עסקאות במניות עצמן מטפסי 4 ו-5. עסקאות בניירות נגזרים, כמו הענקת אופציות, אינן נכללות."
      />
    );
  }

  const list = showAll ? transactions : transactions.slice(0, 20);
  const src = transactions.find(t => t.source_url);
  // The last 90 days by kind (sprint 14, 4.3): the answer first, the list
  // under it, folded.
  const recent = recentByKind(transactions);
  const recentTotal = recent.reduce((a, r) => a + r.count, 0);
  const partial = recentIsPartial(transactions);

  return (
    <div>
      <div style={{ marginBottom: T.space.md }}>
        <div style={{ ...TYPE.headingSub, marginBottom: 6 }}>
          <LTR className="si-num">90</LTR> הימים האחרונים
        </div>
        {recentTotal === 0 ? (
          <div style={{ ...TYPE.bodySmall }}>לא דווחו עסקאות בעלי עניין ב-<LTR className="si-num">90</LTR> הימים האחרונים</div>
        ) : (
          <div style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, lineHeight: 1.7 }}>
            {partial && <span style={{ fontSize: 13, fontWeight: 600, color: UI.text.muted }}>לפחות </span>}
            {recent.map((r, i) => (
              <span key={r.code}>{i ? " · " : ""}<LTR className="si-num">{r.count}</LTR> <span title={codeNote(r.code)}>{r.label}</span></span>
            ))}
          </div>
        )}
        <div style={{ ...TYPE.caption, marginTop: 4 }}>
          לפי תאריך העסקה בדיווח · כל שורה בטופס נספרת בנפרד{partial ? <> · מתוך <LTR className="si-num">{TRANSACTIONS_LIMIT}</LTR> הדיווחים האחרונים שנקראו</> : null} · מקור: <LTR>SEC EDGAR</LTR> · טפסי <LTR>4</LTR> ו-<LTR>5</LTR>
        </div>
      </div>

      {/* Stated as a finding, never as a signal. What an insider intended by a
          trade is not in a form 4, and the app does not speculate about it. */}
      <div style={{
        background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`,
        borderRadius: T.radius.lg, padding: `${T.scale[2]}px ${T.scale[4]}px`,
        fontSize: 12.5, color: UI.text.secondary, lineHeight: 1.65, marginBottom: T.space.md,
      }}>
        רשימת העסקאות כפי שדווחו ל-SEC, לפי תאריך וקוד עסקה. קוד עסקה מתאר את סוג
        הפעולה בלבד · מכירה יכולה לנבוע מתוכנית מכירה שנקבעה מראש, מחבות מס או
        מסיבות אישיות, והדיווח אינו מציין מהן.
      </div>

      <button onClick={() => setListOpen(o => !o)} aria-expanded={listOpen}
        style={{ background: "transparent", border: "none", padding: "6px 0", minHeight: 44, fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit", marginBottom: T.scale[1] }}>
        {listOpen ? "הסתר את הרשימה" : <>הרשימה המלאה · <LTR className="si-num">{transactions.length}</LTR> עסקאות</>}
      </button>

      {listOpen && <div style={{ display: "flex", flexDirection: "column", gap: T.scale[1] }}>
        {list.map((t, i) => (
          <div key={`${t.owner_name}-${t.transaction_date}-${i}`} style={{
            background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`,
            borderRadius: T.radius.lg, padding: `${T.scale[2]}px ${T.scale[4]}px`,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: T.space.sm }}>
              <span style={{ ...TYPE.bodySmall, fontWeight: 700, color: UI.text.primary, minWidth: 0 }}>
                <LTR title={t.owner_name}>{displayName(t.owner_name)}</LTR>
              </span>
              <span style={{ fontSize: 11.5, color: UI.text.muted, flexShrink: 0 }}>
                {dateNumeric(t.transaction_date)}
              </span>
            </div>

            {t.owner_title && (
              <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 1 }}>
                <Title raw={t.owner_title}/>
              </div>
            )}

            <div style={{
              display: "flex", alignItems: "center", gap: T.scale[2],
              flexWrap: "wrap", marginTop: T.scale[1],
            }}>
              {/* Neutral by rule. A transaction code is a category, and green
                  and red in this product mean a number that rose or fell —
                  never a kind of event, and never an implied verdict on it. */}
              <span style={{
                background: UI.state.surface, color: UI.state.fg,
                border: `1px solid ${UI.state.line}`, borderRadius: T.radius.pill,
                padding: "2px 10px", fontSize: 11.5, fontWeight: 700,
              }} title={codeNote(t.transaction_code)}>
                {describeCode(t.transaction_code)}
              </span>
              <LTR className="si-num" style={{ fontSize: 13, fontWeight: 700, color: UI.text.primary }}>
                {shares(t.shares)}
              </LTR>
              <span style={{ fontSize: 12, color: UI.text.muted }}>מניות</span>
              {t.price_per_share != null && t.price_per_share > 0 && (
                <span style={{ fontSize: 12, color: UI.text.muted }}>
                  לפי <Amount value={Number(t.price_per_share)} currency="USD" decimals={2} size={12} weight={600} color={UI.text.muted}/>
                </span>
              )}
            </div>

            {t.shares_owned_after != null && (
              <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: T.scale[0] }}>
                אחזקה לאחר העסקה <LTR className="si-num">{shares(t.shares_owned_after)}</LTR>
              </div>
            )}
          </div>
        ))}
      </div>}

      {listOpen && transactions.length > list.length && (
        <button onClick={() => setShowAll(true)} style={{
          marginTop: T.space.sm, width: "100%",
          background: "transparent", border: `1px solid ${UI.line.hairline}`,
          borderRadius: T.radius.md, padding: "10px",
          fontSize: 13, fontWeight: 700, color: UI.text.secondary, cursor: "pointer",
        }}>
          הצג את כל {transactions.length} העסקאות
        </button>
      )}

      <SourceLine form="טפסי 4 · 5" filed={src?.filed_date} url={src?.source_url}/>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// The component
// ═════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: "does",    label: "מה החברה עושה",         icon: "building" },
  { id: "money",   label: "מאיפה הכסף",            icon: "coins" },
  { id: "who",     label: "מי מנהל",               icon: "person" },
  { id: "shares",  label: "מה הם עושים עם המניות", icon: "list" },
];

export default function CompanyProfile({ symbol }) {
  const [tab, setTab] = useState("does");
  const data = useCompanyProfileData(symbol);
  const secStatus = useSecStatus(symbol, data !== null && !data?.company);

  if (data === null) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32, gap: 10, color: UI.text.muted, fontSize: 13 }}>
        <span className="si-spin" style={{ width: 18, height: 18, border: `2px solid ${UI.line.hairline}`, borderTopColor: UI.interactive, borderRadius: "50%", display: "inline-block" }}/>
        טוען פרופיל חברה...
      </div>
    );
  }

  // Outside the covered set there is nothing to show and nothing to guess.
  if (!data.company) {
    return <div><SecEmpty symbol={symbol} status={secStatus} icon="building"/></div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: T.space.sm }}>
      {/* An underline row, deliberately unlike the filled chips of the panel
          above it: two tab bars that look alike read as one broken bar. */}
      {/* Two by two: in one scrolling row the fourth tab was cut at the edge
          of a 390px screen with nothing to say there was more (2026-09-26). */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: T.scale[2],
        borderBottom: `1px solid ${UI.line.hairline}`,
      }}>
        {TABS.map(t => {
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "transparent", border: "none",
              borderBottom: `2px solid ${on ? UI.text.primary : "transparent"}`,
              padding: "8px 2px", marginBottom: -1, minHeight: 44,
              fontSize: 13, fontWeight: on ? 800 : 600, fontFamily: "inherit",
              color: on ? UI.text.primary : UI.text.muted,
              cursor: "pointer", textAlign: "start",
              display: "flex", alignItems: "center", gap: 5,
            }}>
              <Icon name={t.icon} size={14} color={on ? UI.text.primary : UI.text.muted}/>
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ paddingTop: T.scale[1] }}>
        {tab === "does"   && <WhatItDoes business={data.business}/>}
        {tab === "money"  && <WhereTheMoneyComesFrom segments={data.segments}/>}
        {tab === "who"    && <WhoRunsIt officers={data.officers}/>}
        {tab === "shares" && <WhatTheyDo transactions={data.transactions}/>}
      </div>
    </div>
  );
}
